import React, { useState } from 'react';
import { customerService } from '../../services/customerService';
import { formatters } from '../../utils/formatters';
import { Gift, Check, X } from 'lucide-react';

const ReferralSystemTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);

  const handleUpdateExistingCustomers = async () => {
    setUpdating(true);
    setUpdateResult(null);

    try {
      const updatedCount = await customerService.updateExistingCustomersWithReferralCodes();
      setUpdateResult({
        success: true,
        message: `${updatedCount} clientes atualizados com códigos de indicação`,
        count: updatedCount
      });
    } catch (error) {
      setUpdateResult({
        success: false,
        message: `Erro: ${error.message}`
      });
    } finally {
      setUpdating(false);
    }
  };

  const runTests = async () => {
    setLoading(true);
    const results = [];

    try {
      // Teste 1: Gerar código de indicação
      results.push({ test: 'Geração de código', status: 'running' });
      const testName = 'João Teste';
      const testCpf = '12345678901';
      const referralCode = customerService.generateReferralCode(testName, testCpf);
      
      if (referralCode && referralCode.length >= 8) {
        results[0] = { test: 'Geração de código', status: 'success', result: referralCode };
      } else {
        results[0] = { test: 'Geração de código', status: 'error', error: 'Código inválido' };
      }

      // Teste 2: Validar código existente (simulação)
      results.push({ test: 'Validação de código', status: 'running' });
      try {
        // Este teste falhará porque o código não existe no banco, mas mostra que a função funciona
        await customerService.checkReferralCode(referralCode, 'fake-company-id');
        results[1] = { test: 'Validação de código', status: 'success', result: 'Função de validação operacional' };
      } catch (error) {
        results[1] = { test: 'Validação de código', status: 'expected_error', result: 'Código não encontrado (esperado)' };
      }

      // Teste 3: Formatar CPF
      results.push({ test: 'Formatação de dados', status: 'running' });
      const formattedCpf = formatters.formatCpf(testCpf);
      if (formattedCpf === '123.456.789-01') {
        results[2] = { test: 'Formatação de dados', status: 'success', result: formattedCpf };
      } else {
        results[2] = { test: 'Formatação de dados', status: 'error', error: 'Formatação incorreta' };
      }

    } catch (error) {
      results.push({ test: 'Erro geral', status: 'error', error: error.message });
    }

    setTestResults(results);
    setLoading(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
      case 'expected_error':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'error':
        return <X className="w-5 h-5 text-red-600" />;
      case 'running':
        return <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
      case 'expected_error':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'running':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Gift className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Sistema de Indicações - Teste</h1>
              <p className="text-gray-600">Validação das funcionalidades do sistema de referral</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleUpdateExistingCustomers}
              disabled={updating}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {updating ? 'Atualizando...' : 'Atualizar Clientes'}
            </button>
            <button
              onClick={runTests}
              disabled={loading}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Executando...' : 'Executar Testes'}
            </button>
          </div>
        </div>

        {updateResult && (
          <div className={`border rounded-lg p-4 mb-6 ${updateResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center">
              {updateResult.success ? (
                <Check className="w-5 h-5 text-green-600 mr-2" />
              ) : (
                <X className="w-5 h-5 text-red-600 mr-2" />
              )}
              <p className={updateResult.success ? 'text-green-700' : 'text-red-700'}>
                {updateResult.message}
              </p>
            </div>
          </div>
        )}

        {testResults.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800">Resultados dos Testes</h2>
            {testResults.map((result, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 ${getStatusColor(result.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(result.status)}
                    <span className="ml-3 font-medium text-gray-800">{result.test}</span>
                  </div>
                  <div className="text-sm">
                    {result.result && (
                      <span className="text-gray-600">{result.result}</span>
                    )}
                    {result.error && (
                      <span className="text-red-600">{result.error}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Funcionalidades Implementadas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center text-green-600">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">Geração automática de códigos únicos</span>
              </div>
              <div className="flex items-center text-green-600">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">Validação de códigos de indicação</span>
              </div>
              <div className="flex items-center text-green-600">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">Processamento automático de bonificações</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center text-green-600">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">Interface de configuração para empresas</span>
              </div>
              <div className="flex items-center text-green-600">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">Exibição de códigos na consulta pública</span>
              </div>
              <div className="flex items-center text-green-600">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">Sistema de cópia facilitada</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Como Funciona o Sistema de Indicações</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
            <li>Cada cliente recebe automaticamente um código único ao se cadastrar</li>
            <li>O código é baseado no nome + CPF + timestamp para garantir unicidade</li>
            <li>Novos clientes podem informar um código durante o cadastro</li>
            <li>O sistema valida o código e processa as bonificações automaticamente</li>
            <li>Tanto quem indica quanto quem é indicado recebem pontos configuráveis</li>
            <li>As empresas podem ativar/desativar e configurar valores na tela de configurações</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ReferralSystemTest;
